import type { Workspace } from '@shared/domain/permissions'
import { statusDictionary, type StatusEntity, type StatusMeta } from './dictionary'

export interface ResolvedStatus extends StatusMeta {
  /** the code actually displayed (after masking) */
  code: string
  /** the code the API sent */
  rawCode: string
  known: boolean
}

export interface StatusAnomaly {
  kind: 'unknown' | 'notVisible'
  entity: StatusEntity
  status: string
  workspace: Workspace
}

let reportAnomaly = (a: StatusAnomaly) => {
  console.warn(`[status] ${a.kind} ${a.entity}:${a.status} for workspace ${a.workspace}`)
}

/** Hook observability in at startup (e.g. a Sentry breadcrumb). */
export function setStatusAnomalyReporter(fn: (a: StatusAnomaly) => void) {
  reportAnomaly = fn
}

export function getStatusMeta(entity: StatusEntity, status: string): StatusMeta | undefined {
  const table: Record<string, StatusMeta> = statusDictionary[entity]
  return table[status]
}

/**
 * Resolve a status for display in a workspace. Applies `maskAs` and reports statuses the
 * backend should not have sent: visibility is enforced by the API, so a mismatch is a bug.
 */
export function resolveStatus(
  entity: StatusEntity,
  status: string,
  workspace: Workspace,
): ResolvedStatus {
  const meta = getStatusMeta(entity, status)
  if (!meta) {
    reportAnomaly({ kind: 'unknown', entity, status, workspace })
    return {
      code: status,
      rawCode: status,
      known: false,
      labelKey: 'status.unknown',
      tone: 'neutral',
      stage: 0,
      visibleTo: [],
    }
  }
  if (!meta.visibleTo.includes(workspace)) {
    reportAnomaly({ kind: 'notVisible', entity, status, workspace })
  }
  const masked = meta.maskAs?.[workspace]
  const maskedMeta = masked ? getStatusMeta(entity, masked) : undefined
  if (masked && maskedMeta) return { ...maskedMeta, code: masked, rawCode: status, known: true }
  return { ...meta, code: status, rawCode: status, known: true }
}

export function isTerminalStatus(entity: StatusEntity, status: string): boolean {
  return getStatusMeta(entity, status)?.terminal === true
}

/** Status codes a workspace may filter by (masked codes are excluded). */
export function statusesVisibleTo(entity: StatusEntity, workspace: Workspace): string[] {
  const table: Record<string, StatusMeta> = statusDictionary[entity]
  return Object.entries(table)
    .filter(([, m]) => m.visibleTo.includes(workspace) && !m.maskAs?.[workspace])
    .map(([code]) => code)
}
